import { request, streamSSE } from './core'
import type {
  AwsConnectionOutput,
  AwsConnectionPayload,
  AwsInventoryResponse,
  AwsPlatformIdentity,
  PendingFinding,
} from './types'

export const awsImportApi = {
  // --- connection CRUD ---

  getAwsConnection: (slug: string) =>
    request<{ connection: AwsConnectionOutput | null }>(
      `/api/assessments/${slug}/aws-import/connection`,
    ),

  saveAwsConnection: (slug: string, payload: AwsConnectionPayload) =>
    request<AwsConnectionOutput>(
      `/api/assessments/${slug}/aws-import/connection`,
      {
        method: 'PUT',
        body: JSON.stringify(payload),
        headers: { 'Content-Type': 'application/json' },
      },
    ),

  /** Either/or: keys present → tests exactly what was entered (pre-save);
   *  keys blank → the server decrypts and tests the SAVED credentials
   *  (`awsAccountId` picks the account row in per-account mode). */
  testAwsCredentials: (
    slug: string,
    accessKey: string,
    secretKey: string,
    expectedAccountId?: string,
    sessionToken?: string,
    awsAccountId?: string,
  ) =>
    request<{
      ok: boolean
      account_id: string | null
      arn: string | null
      user_id: string | null
      reason: string | null
      expired: boolean
      /** Which pair was tested: keys from the form, or the saved ones. */
      source?: 'entered' | 'stored'
    }>(`/api/assessments/${slug}/aws-import/connection/test`, {
      method: 'POST',
      body: JSON.stringify({
        access_key: accessKey,
        secret_key: secretKey,
        expected_account_id: expectedAccountId,
        session_token: sessionToken,
        aws_account_id: awsAccountId,
      }),
      headers: { 'Content-Type': 'application/json' },
    }),

  /** assume_role mode: performs the AssumeRole itself, proving the customer's
   *  trust policy admits us. Separate from `testAwsCredentials` because it
   *  tests a role rather than a key pair — and because bolting two more
   *  positional args onto that signature was already one too many. */
  testAwsAssumeRole: (slug: string, roleArn: string, expectedAccountId?: string) =>
    request<{
      ok: boolean
      account_id: string | null
      arn: string | null
      user_id: string | null
      reason: string | null
      expired: boolean
      source?: 'entered' | 'stored'
    }>(`/api/assessments/${slug}/aws-import/connection/test`, {
      method: 'POST',
      body: JSON.stringify({
        // No external id: the server always assumes with the company's own.
        role_arn: roleArn,
        expected_account_id: expectedAccountId,
      }),
      headers: { 'Content-Type': 'application/json' },
    }),

  /** What the customer's role trust policy must name: the platform's own AWS
   *  account (same for every company) and THIS company's ExternalId. */
  getAwsPlatformIdentity: (slug: string) =>
    request<AwsPlatformIdentity>(`/api/assessments/${slug}/aws-import/identity`),

  // --- import lifecycle ---

  /** The stored inventory from the most recent scan (migration 0031). One row
   *  is persisted per run; `snapshot` is null when never scanned. */
  getAwsInventory: (slug: string) =>
    request<AwsInventoryResponse>(`/api/companies/${slug}/aws-import/inventory`),

  getAwsImport: (slug: string) =>
    request<{
      status: 'none' | 'running' | 'done' | 'failed' | 'skipped'
      error?: string | null
      started_at?: string | null
      export_id?: string | null
      findings: PendingFinding[]
      revision: number
    }>(`/api/assessments/${slug}/aws-import`),

  streamAwsImport: (slug: string, onEvent: (evt: Record<string, unknown>) => void) =>
    streamSSE(`/api/assessments/${slug}/aws-import/run`, onEvent),

  applyAwsFindings: (slug: string, fieldPaths: string[], revision?: number) =>
    request<{ applied: string[]; remaining: number; revision: number }>(
      `/api/assessments/${slug}/aws-import/findings/apply`,
      {
        method: 'POST',
        body: JSON.stringify({ field_paths: fieldPaths }),
        headers: { 'Content-Type': 'application/json' },
      },
      { ifMatch: revision },
    ),

  dismissAwsFindings: (slug: string, fieldPaths: string[], revision?: number) =>
    request<{ dismissed: string[]; remaining: number; revision: number }>(
      `/api/assessments/${slug}/aws-import/findings/dismiss`,
      {
        method: 'POST',
        body: JSON.stringify({ field_paths: fieldPaths }),
        headers: { 'Content-Type': 'application/json' },
      },
      { ifMatch: revision },
    ),

  /** Company-level "skip for now" on the Get-started onboarding: marks the
   *  COMPANY's aws_import_status='skipped' so the hub unlocks module starts.
   *  (The legacy assessment-scoped /skip that advanced the AI module's
   *  workflow status is gone from the FE — new assessments start at intake.) */
  skipCompanyAwsOnboarding: (slug: string) =>
    request<{ ok: boolean; aws_import_status: string }>(
      `/api/companies/${slug}/aws-import/skip`,
      { method: 'POST' },
    ),

  // --- AWS SSO (IAM Identity Center) device-flow login ---

  ssoStart: (startUrl: string, region: string) =>
    _post<{
      region: string
      client_id: string
      client_secret: string
      device_code: string
      user_code: string
      verification_uri_complete: string
      verification_uri: string
      interval: number
      expires_in: number
    }>('/api/aws-sso/start', { start_url: startUrl, region }),

  ssoToken: (p: { clientId: string; clientSecret: string; deviceCode: string; region: string }) =>
    _post<{ status: string; access_token: string | null; expires_in: number | null; reason: string | null }>(
      '/api/aws-sso/token',
      { client_id: p.clientId, client_secret: p.clientSecret, device_code: p.deviceCode, region: p.region },
    ),

  ssoAccounts: (accessToken: string, region: string) =>
    _post<{ accounts: { account_id: string; account_name: string; email: string | null; roles: string[] }[] }>(
      '/api/aws-sso/accounts',
      { access_token: accessToken, region },
    ),

  ssoCredentials: (accessToken: string, region: string, accountId: string, roleName: string) =>
    _post<{ access_key_id: string; secret_access_key: string; session_token: string; expiration: number | null }>(
      '/api/aws-sso/credentials',
      { access_token: accessToken, region, account_id: accountId, role_name: roleName },
    ),

  ssoProvisionAccessKeys: (p: {
    tempAccessKey: string
    tempSecretKey: string
    tempSessionToken: string
    accountId: string
    expirationDays: number
  }) =>
    _post<{
      access_key_id: string
      secret_access_key: string
      expiration: string
      user_name: string
    }>('/api/aws-sso/provision-access-keys', {
      temp_access_key: p.tempAccessKey,
      temp_secret_key: p.tempSecretKey,
      temp_session_token: p.tempSessionToken,
      account_id: p.accountId,
      expiration_days: p.expirationDays,
    }),
}

/** Small JSON-POST helper for the SSO endpoints (not assessment-scoped). */
function _post<T>(path: string, body: unknown): Promise<T> {
  return request<T>(path, {
    method: 'POST',
    body: JSON.stringify(body),
    headers: { 'Content-Type': 'application/json' },
  })
}
